import React, { useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FileUpload from '@fuse/core/FileUpload';
import handleError from '@fuse/core/errorHandler';
import MarkdownEditor from '@uiw/react-markdown-editor';
import useMessage from 'src/app/hooks/useMessage';
import { Close } from '@mui/icons-material';
import { Button, IconButton, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEditArticleMutation, useGetArticleByIdQuery } from 'src/app/services/articlesApi';

const NewsEdit = ({ post, onOpen, onClose, refetchList, onEdit }) => {
  const { t } = useTranslation('translations');
  const [editArticle, { isLoading }] = useEditArticleMutation();
  const { showMessage } = useMessage();
  const { data } = useGetArticleByIdQuery({ id: post?.id }, { skip: !post?.id || !onOpen });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm();

  useEffect(() => {
    if (data) {
      reset({
        ...data,
        textUz: data.textUz || '',
        isPonarama: data.isPanorama,
      });
    }
  }, [data]);

  const onSubmit = (formValues) => {
    if (!formValues.text || !formValues.textUz) {
      showMessage({
        message: t('FillNewsText'),
        autoHideDuration: 3000,
        variant: 'error',
      });
      return;
    }

    formValues.articleFileIds =
      formValues.articleFileIds?.length > 0
        ? formValues.articleFileIds.map((media) => media.id)
        : post.fileList?.map((file) => file.id) || [];

    editArticle(formValues)
      .unwrap()
      .then(() => {
        handleClose();
        showMessage({
          message: t('NewsSuccessfullyEdited'),
          autoHideDuration: 3000,
          variant: 'success',
        });
        onEdit();
        refetchList();
      })
      .catch(handleError);
  };

  const handleClose = (_event, reason) => {
    if (reason === 'backdropClick') return;
    onClose();
    reset();
  };

  return (
    <Dialog open={onOpen} onClose={handleClose} maxWidth="xl">
      <DialogTitle className="relative">
        <IconButton onClick={handleClose} className="absolute top-0 right-0">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div className="flex flex-col md:flex-row">
          <form onSubmit={handleSubmit(onSubmit)}>
            <Typography component="p" className="mb-20" variant="h6">
              {t('EditPost')}
            </Typography>
            <div className="flex flex-col md:flex-row gap-20 mb-20">
              <div>
                <Controller
                  name="titleUz"
                  control={control}
                  rules={{ required: 'Sarlavha majburiy' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      className="mb-10"
                      fullWidth
                      label={'Sarlavha (uzb)'}
                      variant="outlined"
                      error={!!errors.titleUz}
                      helperText={errors.titleUz?.message}
                    />
                  )}
                />
                <Typography variant="subtitle1" className="mb-10">
                  Yangilik matni (Uz)
                </Typography>
                <Controller
                  name="textUz"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <MarkdownEditor {...field} className="w-full min-h-[400px] md:max-w-[400px]" />
                  )}
                />
              </div>
              <div>
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: 'Заголовок обязателен' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      className="mb-10"
                      fullWidth
                      label={'Заголовок (rus)'}
                      variant="outlined"
                      error={!!errors.title}
                      helperText={errors.title?.message}
                    />
                  )}
                />
                <Typography variant="subtitle1" className="mb-10">
                  Текст новости (Ру)
                </Typography>
                <Controller
                  name="text"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <MarkdownEditor {...field} className="w-full min-h-[400px] md:max-w-[400px]" />
                  )}
                />
              </div>
            </div>
            <Controller
              name="isPonarama"
              control={control}
              defaultValue={data?.isPanorama || false}
              render={({ field }) => (
                <div className="flex items-center gap-10 mb-10">
                  <input
                    type="checkbox"
                    id="isPonarama"
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={!!watch('articleFileIds')?.length}
                  />
                  <label htmlFor="isPanorama">{t('Panorama')}</label>
                </div>
              )}
            />
            <FileUpload name="articleFileIds" control={control} type={6} multiple />
            <div className="flex items-stretch justify-end gap-10">
              <Button variant="contained" className="dark:bg-lightblack" onClick={handleClose}>
                {t('Cancel')}
              </Button>
              <Button
                variant="contained"
                color="success"
                className="dark:text-white"
                type="submit"
                disabled={isLoading}
              >
                {t('Edit')}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewsEdit;
